import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchBusinessDomain, updateBusinessDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

export default function BusinessKpiEditPage() {
  const { businessDomainId, businessKpiId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [domain, setDomain] = useState(null);
  const [form, setForm] = useState({ indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" });
  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);
  const [currentIndicatorName, setCurrentIndicatorName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchBusinessDomain(businessDomainId);
        setDomain(d);
        const idx = parseInt(businessKpiId, 10);
        const ind = (d.indicators || [])[idx];
        if (ind) {
          setCurrentIndicatorName(ind.indicatorName || "");
          setForm({
            indicatorName: ind.indicatorName || "",
            unit: ind.unit || "",
            measuringMethod: ind.measuringMethod || "",
            evaluationStandard: ind.evaluationStandard || "",
            description: ind.description || "",
          });
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
  }, [businessDomainId, businessKpiId]);

  const isLocked = currentIndicatorName ? usedKpiNames.has(currentIndicatorName) : false;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const idx = parseInt(businessKpiId, 10);
      const newIndicators = [...(domain.indicators || [])];
      newIndicators[idx] = form;
      await updateBusinessDomain({ ...domain, indicators: newIndicators });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/business-kpis/${businessKpiId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("businessKpi.edit.title")}</Typography>
      {/* KPI編集時のInfo（案D）：使用中は🔒アラート、未使用は注意Info */}
      <KpiConstraintAlert variant={isLocked ? "edit-locked" : "edit-unused"} />
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required label={t("businessKpi.indicatorName")} name="indicatorName" value={form.indicatorName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required label={t("businessKpi.unit")} name="unit" value={form.unit} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("businessKpi.measuringMethod")} name="measuringMethod" value={form.measuringMethod} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("businessKpi.evaluationStandard")} name="evaluationStandard" value={form.evaluationStandard} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("common.description")} name="description" value={form.description} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!domain || isLocked}>{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/business-kpis/${businessKpiId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
